import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Modal } from "../components/ui";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";

const MONTHS = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
const WEEKDAYS = ["Dom","Lun","Mar","Mié","Jue","Vie","Sáb"];

/**
 * Custom monthly FullCalendar-style component.
 * Used for Field Walks, Talks, Inductions.
 */
export default function Calendar({ module = "all", title = "Calendario", testId = "calendar" }) {
  const [events, setEvents] = useState([]);
  const [date, setDate] = useState(new Date());
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({});

  async function load() {
    const { data } = await api.get("/calendar/events", { params: { module } });
    setEvents(data);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [module]);

  const year = date.getFullYear();
  const month = date.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const eventsByDate = {};
  events.forEach((e) => {
    if (!eventsByDate[e.start]) eventsByDate[e.start] = [];
    eventsByDate[e.start].push(e);
  });

  function openDay(d) {
    const ds = `${year}-${String(month+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
    setSelected(ds);
  }

  function openNewForm(dateStr) {
    setForm({ date: dateStr, status: "Programada" });
    setShowForm(true);
  }

  async function saveEvent() {
    const collection = module === "all" ? "talks" : module;
    const payload = { ...form };
    if (collection === "field_walks") payload.area = form.title || form.area;
    if (collection === "inductions") payload.title = form.title;
    if (collection === "talks") payload.topic = form.title;
    if (collection === "drills") { payload.scheduled_date = payload.date; delete payload.date; payload.type = form.title; }
    await api.post(`/data/${collection}`, payload);
    setShowForm(false); setForm({});
    load();
  }

  return (
    <div data-testid={testId}>
      <div className="card">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <button className="btn btn-sm" onClick={() => setDate(new Date(year, month - 1, 1))}><ChevronLeft size={14} /></button>
            <div className="font-semibold text-lg">{MONTHS[month]} {year}</div>
            <button className="btn btn-sm" onClick={() => setDate(new Date(year, month + 1, 1))}><ChevronRight size={14} /></button>
            <button className="btn btn-sm" onClick={() => setDate(new Date())}>Hoy</button>
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => openNewForm(new Date().toISOString().slice(0,10))} data-testid={`${testId}-add`}>
            <Plus size={14} /> Nuevo evento
          </button>
        </div>

        <div className="grid" style={{ gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
          {WEEKDAYS.map((w) => <div key={w} className="text-xs font-semibold text-center p-2 text-dim">{w}</div>)}
          {cells.map((d, i) => {
            const ds = d ? `${year}-${String(month+1).padStart(2,"0")}-${String(d).padStart(2,"0")}` : null;
            const evs = ds ? (eventsByDate[ds] || []) : [];
            return (
              <div
                key={i}
                onClick={() => d && openDay(d)}
                style={{
                  minHeight: 90,
                  background: d ? "var(--bg-2)" : "transparent",
                  border: d ? "1px solid var(--border)" : "none",
                  borderRadius: 6,
                  padding: 6,
                  cursor: d ? "pointer" : "default",
                  opacity: d ? 1 : 0.3,
                }}
              >
                {d && <div className="text-xs font-semibold mb-1">{d}</div>}
                {evs.slice(0, 3).map((e, j) => (
                  <div key={j} className="text-xs mb-1 p-1 rounded" style={{
                    background: e.color, color: "white", overflow: "hidden",
                    textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>{e.title}</div>
                ))}
                {evs.length > 3 && <div className="text-xs text-dim">+{evs.length - 3} más</div>}
              </div>
            );
          })}
        </div>
      </div>

      <Modal open={!!selected} onClose={() => setSelected(null)} title={`Eventos del ${selected}`} testId={`${testId}-day`}>
        <div className="grid gap-2">
          {(eventsByDate[selected] || []).map((e, i) => (
            <div key={i} className="p-3 rounded" style={{ background: "var(--bg-2)" }}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold">{e.title}</div>
                  <div className="text-xs text-dim">{e.module} · {e.extendedProps?.status}</div>
                </div>
                <span className="badge" style={{ background: e.color, color: "white" }}>{e.module}</span>
              </div>
            </div>
          ))}
          <button className="btn btn-primary mt-3" onClick={() => { setSelected(null); openNewForm(selected); }}>
            <Plus size={14} /> Nuevo evento ese día
          </button>
        </div>
      </Modal>

      <Modal open={showForm} onClose={() => setShowForm(false)} title="Nuevo evento de calendario" testId={`${testId}-form`}>
        <div className="grid gap-3">
          <div>
            <label className="label">Tipo</label>
            <select className="select" value={module === "all" ? "talks" : module} disabled>
              <option value="talks">Plática</option>
              <option value="field_walks">Recorrido de campo</option>
              <option value="inductions">Inducción</option>
              <option value="drills">Simulacro</option>
            </select>
          </div>
          <div>
            <label className="label">Título *</label>
            <input className="input" value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid={`${testId}-title`} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">Fecha *</label><input type="date" className="input" value={form.date || ""} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
            <div><label className="label">Horario</label><input className="input" value={form.time || ""} onChange={(e) => setForm({ ...form, time: e.target.value })} placeholder="10:00 - 12:00" /></div>
          </div>
          <div><label className="label">Responsable / Presentador</label><input className="input" value={form.presenter || ""} onChange={(e) => setForm({ ...form, presenter: e.target.value, responsible: e.target.value })} /></div>
          <div className="flex justify-end gap-2">
            <button className="btn" onClick={() => setShowForm(false)}>Cancelar</button>
            <button className="btn btn-primary" onClick={saveEvent} data-testid={`${testId}-save`}>Guardar</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
